/** The Figures tab of a scope table: one filter line over one bordered grid - ADR-0212 decision 2.
 *
 * Every row is a figure the inventory holds. The columns are labels, numbers and chips; nothing on
 * this tab explains itself in a sentence, and the explanations sit behind the info controls on the
 * columns that need one (`infoText.ts`, sections `scope1` and `scope2`).
 *
 * **Rule 3 stays where it was.** The tCO2e cell is a button that opens the figure's receipt; the row
 * opens the record page (the data, the document, the calculation and the approvals of the activity
 * behind the figure). A figure whose evidence chain is incomplete is *still listed* here, with the
 * chain drawn beside it and the status saying where it breaks - the table is the register, and the
 * Scope 3 list (`splitByReceipt`) is the place that withholds, because it states no total.
 *
 * **The list is the server's, a page at a time.** The filters, the search and the order are asked of
 * `GET /figures` (`cutOfFilters`), the filter chips are offered from `GET /figures/facets`, and the
 * rows are read `FIGURES_PAGE` at a time with "Show more": no screen loads the whole window, so none
 * fails when it holds ten thousand figures. The filter line says "N of M" - N rows read, M the server's
 * count of the selection - and the sort order is the server's, so it is the same on every page.
 *
 * **The footer total is the server's.** The totals row is the server's string, shown only when no
 * filter is narrowing the list and every figure is loaded: a total beside a filtered list would be a
 * figure with no receipt, and a total beside a partial list would be a wrong one. The one sum made on
 * this page is a group's subtotal (`summable` on the tonnes columns), exact decimal arithmetic over the
 * figures the group holds - drawn only when every figure of the selection is loaded, because a group
 * is a part of the loaded rows and its subtotal over a part would be a wrong number.
 *
 * **The table power features** (bulk bar, filter chips mirrored in the URL, saved views, quick-peek,
 * column menus, density, grouping, the chooser and Export) come from `useGridPower`; this file supplies
 * what they cannot know - the columns' plain values, the starter views and the peek's content. The
 * bulk bar offers Export only: no action is invented, and each figure keeps its receipt link in the
 * cell and in the peek.
 *
 * The Scope 2 market-based column is each row's `market_figure`: the market inventory's figure for the
 * same activity record, which the server puts beside it (`include_market`). A record with no market
 * figure says **No figure**, which is not a zero.
 */

import type { Icon } from "@phosphor-icons/react";
import { useMemo } from "react";
import type { ReactNode } from "react";

import { useMe } from "../../api/auth";
import { mayEnterData } from "../../shell/routeTable";
import { FIGURES_PAGE, useFigureFacets, useFiguresPaged } from "../../api/queries";
import type { Figure, FigureCut, FigureCutRead, Scope2Method, Window } from "../../api/queries";
import type { GridColumn, SavedView } from "../../components/ui";
import {
  ActionButton,
  BASIS_WORD,
  BasisChip,
  EmptyState,
  FilterLine,
  GridTable,
  InfoPopover,
  QualityMeter,
  StatusChip,
  isBasis,
  useGridPower,
  useUrlFilters,
} from "../../components/ui";
import { computedQuantity, exactTonnes, periodShort, quantity, tonnes, unitLabel } from "../../lib/format";
import { ChainDots } from "../figures/ChainDots";
import { FigurePeek } from "./FigurePeek";
import { STATUS_WORD, TIER_WORD, cutOfFilters, facetFilterDefs, categoryWords, periodWords, statusOf, words } from "./model";
import type { FigureStatus, Scope } from "./model";
import styles from "./scopeTable.module.css";

const STATUS_TONE: Record<FigureStatus, "ok" | "warn" | "crit" | "neutral"> = {
  verified: "ok",
  awaiting: "warn",
  not_verified: "neutral",
  gap: "crit",
};

/** Who acts next, for the one status that is waiting on a person. Said on hover; the word stays the
 * word. */
const STATUS_TITLE: Partial<Record<FigureStatus, string>> = {
  awaiting: "Needs a different Admin or Approver to accept it.",
};

/** The views every scope table ships, locked: a person can duplicate one and change the copy. Each is a
 * status the table already filters by, so nothing here is a claim of its own. */
const view = (id: string, name: string, status: FigureStatus): SavedView => ({
  id,
  name,
  config: { filters: { search: "", values: { status: [status] } }, sorts: [], hidden: [], groupBy: null, pinned: [] },
});
const STARTER_VIEWS: readonly SavedView[] = [
  view("awaiting", "Awaiting accept", "awaiting"),
  view("gaps", "Evidence gaps", "gap"),
];

type Grade = "A" | "B" | "C" | "D";
const isGrade = (value: string): value is Grade => value === "A" || value === "B" || value === "C" || value === "D";

export interface FiguresTableProps {
  scope: Scope;
  /** The window the figures are for. Nothing is asked of the server without one. */
  window: Window | undefined;
  method: Scope2Method;
  loading?: boolean;
  /** The server's total for the scope, in kilograms, or `undefined` when it states none. */
  serverTotalKg: string | undefined;
  /** Scope 2 only: draw the market-based column, with the market figure the server puts beside each row. */
  showMarket?: boolean;
  /** What the first column is called: Fuel, Carrier. */
  carrierLabel: string;
  onReceipt: (calculationId: string) => void;
  onOpenRecord: (activityRecordId: string) => void;
  /** The empty state's one sentence, and its one action if the role has one. */
  emptyTitle: string;
  /** What will appear here: one short sentence under the title. */
  emptyBody?: string;
  emptyIcon?: Icon;
  emptyAction?: ReactNode;
  /** The receipt button's test id is `${figureTestPrefix}-${figure.id}`. */
  figureTestPrefix?: string;
  emptyTestId?: string;
  /** Terms the page adds to every read beyond the reader's filters (Scope 3 lists only figures whose
   * evidence chain holds). A table with one lacks figures the footer total counts, so it prints none. */
  fixedCut?: Pick<FigureCut, "evidence">;
}

export interface FiguresTableRead {
  filters: ReactNode;
  table: ReactNode;
  nothingYet: boolean;
  /** The figures read: its `error` and `refetch` are the page's to say, in the table's place. */
  read: FigureCutRead;
  /** The filter chips' options: a failed read leaves the chips without values, and says so here. */
  facetsError: unknown;
  refetchFacets: () => void;
}

export function useFiguresTable({
  scope,
  window,
  method,
  loading: loadingOutside = false,
  serverTotalKg,
  showMarket = false,
  carrierLabel,
  onReceipt,
  onOpenRecord,
  emptyTitle,
  emptyBody,
  emptyIcon,
  emptyAction,
  figureTestPrefix = "figure",
  emptyTestId,
  fixedCut,
}: FiguresTableProps): FiguresTableRead {
  const { data: me } = useMe();
  // The filters live in the address bar (`?f_site=...`), so a filtered list is a link.
  const [filters, setFilters] = useUrlFilters();

  const facets = useFigureFacets(window, method, scope);
  const defs = useMemo(
    () =>
      facetFilterDefs(facets.data, { carrierLabel, withCategory: scope !== "2", withInstrument: showMarket }),
    [facets.data, carrierLabel, scope, showMarket],
  );
  const power = useGridPower({
    place: `scope${scope}`,
    userKey: me?.user_id,
    filters,
    onFiltersChange: setFilters,
    filterDefs: defs,
    resultNoun: "figures",
    starterViews: STARTER_VIEWS,
    exportName: `scope-${scope}-figures`,
  });

  // The list is the server's: its filters, its search and its order. Asking is cheap and the rows
  // come a page at a time, so a table of any size opens at the cost of one page.
  const read = useFiguresPaged(
    window,
    method,
    { ...cutOfFilters(filters, { scope, sorts: power.sorts, includeMarket: showMarket }), ...fixedCut },
    { keepPrevious: true },
  );
  const figures = read.figures;
  const total = read.total;
  const rows = figures;
  const loading = loadingOutside || read.isPending;
  // Every figure of the selection is on screen: nothing the server counts is missing from the rows.
  const complete = !read.canLoadMore && !read.ceilingReached && read.figures.length >= total;

  // The tonnes are a number in a column of their own, right-aligned, and the basis the figure rests on is
  // the next column: a chip beside the number made every row's right edge a different place (the
  // numbers of one column did not line up, U6, 2026-11-03).
  const receiptCell = (figure: Figure, label: string) => (
    <button
      type="button"
      className={styles.figureButton}
      data-testid={`${figureTestPrefix}-${figure.id}`}
      title={`${exactTonnes(figure.kgco2e)} tCO₂e, exact`}
      aria-label={`Receipt for ${figure.facility_name}, ${categoryWords(figure.category)}, ${label} tonnes CO2 equivalent`}
      onClick={() => onReceipt(figure.id)}
    >
      {label}
    </button>
  );

  // Column budgets, in px (`GridColumn.width`): the table is laid out on these tracks, so it fits its
  // card, and the Site column - the one that names the row - takes what is left. A cell is one line, cut
  // with an ellipsis and carrying its whole text as a title.
  // Scope 2 with a market column has two more number columns than any other table here, so its text columns
  // are a little tighter.
  const tight = showMarket;
  const columns: GridColumn<Figure>[] = [
    {
      id: "carrier",
      header: carrierLabel,
      sortable: true,
      width: tight ? "84px" : "104px",
      // "grid electricity" is two words of one name: it wraps and is not cut to "grid electr...".
      wrap: true,
      cell: (f) => words(f.fuel_or_carrier) || "—",
      value: (f) => words(f.fuel_or_carrier),
    },
    {
      id: "site",
      header: "Site",
      sortable: true,
      minWidth: tight ? "112px" : "120px",
      // The site names the row: it wraps to a second line before it is cut.
      wrap: true,
      cell: (f) => f.facility_name,
      value: (f) => f.facility_name,
    },
    ...(scope !== "2"
      ? [
          {
            id: "category",
            header: "Category",
            sortable: true,
            width: "100px",
            cell: (f: Figure) => categoryWords(f.category),
            value: (f: Figure) => categoryWords(f.category),
          } satisfies GridColumn<Figure>,
        ]
      : []),
    {
      id: "period",
      header: "Period",
      sortable: true,
      width: tight ? "100px" : "108px",
      // A part-year period is two dates, and cut to "Dec 9, 2024 - ..." it is no period (G-Wviewer-25).
      wrap: true,
      cell: (f) => periodShort(f.period_start, f.period_end),
      value: (f) => periodWords(f.period_start, f.period_end),
    },
    {
      id: "quantity",
      header: scope === "3" ? "Quantity" : scope === "2" ? "Usage" : "Quantity",
      numeric: true,
      sortable: true,
      width: tight ? "104px" : "144px",
      // "1,369,996.74 kWh (gross calorific value)": the unit and its basis are part of the quantity.
      wrap: true,
      cell: (f) => `${computedQuantity(f.quantity)} ${unitLabel(f.unit)}`,
      value: (f) => `${computedQuantity(f.quantity)} ${unitLabel(f.unit)}`,
    },
    {
      id: "tonnes",
      header: showMarket ? "tCO₂e, location" : "tCO₂e",
      numeric: true,
      sortable: true,
      width: showMarket ? "104px" : "88px",
      cell: (f) => receiptCell(f, tonnes(f.kgco2e)),
      value: (f) => tonnes(f.kgco2e),
      // A group's subtotal: the figures' kilograms added exactly, written as tonnes. Never the footer.
      // Only when every figure of the selection is loaded: a group of a part is a part of a group.
      ...(complete ? { summable: { value: (f: Figure) => f.kgco2e, format: (sum: string) => tonnes(sum) } } : {}),
    },
    {
      // What the figure rests on (P15 item 3): the server's word, never derived here. Its own column, so
      // the tonnes beside it are one column of numbers with one right edge.
      id: "basis",
      header: "Basis",
      width: tight ? "100px" : "108px",
      cell: (f) => (isBasis(f.basis) ? <BasisChip basis={f.basis} /> : "—"),
      value: (f) => (isBasis(f.basis) ? BASIS_WORD[f.basis] : ""),
    },
    ...(showMarket
      ? [
          {
            id: "market",
            header: "tCO₂e, market",
            numeric: true,
            width: "100px",
            value: (f: Figure) => {
              const market = f.market_figure;
              return market ? tonnes(market.kgco2e) : "";
            },
            ...(complete
              ? {
                  summable: {
                    value: (f: Figure) => f.market_figure?.kgco2e,
                    format: (sum: string) => tonnes(sum),
                  },
                }
              : {}),
            cell: (f: Figure) => {
              const market = f.market_figure;
              return market ? receiptCell({ ...f, id: market.id }, tonnes(market.kgco2e)) : (
                <span className={styles.none} data-testid={`no-market-${f.id}`}>
                  No figure
                </span>
              );
            },
          } satisfies GridColumn<Figure>,
          {
            id: "instrument",
            header: "Instrument",
            width: "90px",
            // "Location factor" and "Residual mix" are the instrument: two lines, not "Location f...".
            wrap: true,
            value: (f: Figure) => {
              const tier = f.market_figure?.market_tier;
              return tier ? (TIER_WORD[tier] ?? words(tier)) : "";
            },
            cell: (f: Figure) => {
              const tier = f.market_figure?.market_tier;
              return tier ? (TIER_WORD[tier] ?? words(tier)) : "—";
            },
          } satisfies GridColumn<Figure>,
        ]
      : []),
    {
      id: "grade",
      header: "Grade",
      sortable: true,
      width: tight ? "108px" : "96px",
      cell: (f) => (isGrade(f.quality) ? <QualityMeter grade={f.quality} /> : f.quality),
      value: (f) => f.quality,
    },
    {
      id: "evidence",
      header: "Evidence",
      width: tight ? "90px" : "108px",
      cell: (f) => <ChainDots figure={f} />,
      value: (f) => (f.receipt_is_complete ? "Complete" : (f.evidence_broken_at ?? "Incomplete")),
    },
    {
      id: "status",
      header: "Status",
      // "Awaiting accept" is the one status a person acts on and it was cut to "Awaiting acc" at 104px.
      width: tight ? "132px" : "140px",
      value: (f) => STATUS_WORD[statusOf(f)],
      cell: (f) => {
        const status = statusOf(f);
        return (
          <StatusChip tone={STATUS_TONE[status]} size="sm" title={STATUS_TITLE[status]}>
            {STATUS_WORD[status]}
          </StatusChip>
        );
      },
    },
    // Where a figure came from (uploaded, typed, imported) is not a column: the table is a budget, and
    // eleven columns did not fit a card at 1440. It stays a filter ("Source"), it is in the quick-peek, and
    // its explanation sits on the Basis header, which is the same question asked of the number.
  ];

  const infoFor = (column: GridColumn<Figure>) =>
    column.id === "status" ? (
      <InfoPopover infoKey="scope1.status" />
    ) : column.id === "basis" ? (
      <InfoPopover infoKey="scope1.source" />
    ) : column.id === "grade" ? (
      <InfoPopover infoKey="scope1.grades" />
    ) : null;

  const narrowed = filters.search.trim() !== "" || Object.values(filters.values).some((v) => v.length > 0);
  // Nothing recorded at all: the filter line, its saved views and its tools have nothing to work on,
  // so the table is its header over the one designed empty state (KD, 2026-11-02).
  const nothingYet = !loading && !narrowed && total === 0 && figures.length === 0 && !read.error;
  const nothingWithheld = fixedCut === undefined;
  const unread = Math.max(0, total - read.figures.length);

  return {
    read,
    facetsError: facets.error,
    refetchFacets: () => void facets.refetch(),
    nothingYet,
    filters: nothingYet ? undefined : (
      <FilterLine
        power={power}
        searchLabel="Search this table"
        // While the first page is out there is no count to state: "0 of 0 figures" was printed over
        // the loading rows and read as an empty window (G-P9-09).
        resultCount={loading ? undefined : { shown: rows.length, total, noun: "figures" }}
      />
    ),
    table: (
      <>
      <GridTable
        caption={`Scope ${scope} figures`}
        columns={columns}
        rows={rows}
        rowKey={(f) => f.id}
        rowLabel={(f) => `${f.facility_name}, ${categoryWords(f.category)}, ${periodWords(f.period_start, f.period_end)}`}
        selectable={mayEnterData(me?.role)}
        power={power}
        renderPeek={(f) => <FigurePeek figure={f} scope={scope} onReceipt={onReceipt} />}
        onRowOpen={(f) => onOpenRecord(f.activity_record_id)}
        columnMenu={infoFor}
        loading={loading}
        totals={
          serverTotalKg !== undefined && complete && nothingWithheld && !narrowed && rows.length > 0
            ? { label: "Total", values: { tonnes: tonnes(serverTotalKg) } }
            : undefined
        }
        empty={
          <EmptyState
            title={narrowed ? "No figure matches these filters." : emptyTitle}
            compact
            icon={narrowed ? undefined : emptyIcon}
            action={narrowed ? null : emptyAction}
            testId={narrowed ? undefined : emptyTestId}
          >
            {narrowed ? null : emptyBody}
          </EmptyState>
        }
        testId="scope-figures"
      />
      {rows.length > 0 && !loading && (read.canLoadMore || read.ceilingReached || read.moreError) ? (
        <div className={styles.showMore} data-testid="scope-figures-more" role="status">
          <span>
            {read.ceilingReached
              ? `${quantity(String(read.figures.length))} of ${quantity(String(total))} figures are listed. Narrow the filters or the search to reach the rest.`
              : `${quantity(String(read.figures.length))} of ${quantity(String(total))} figures are listed.`}
          </span>
          {read.moreError ? <span>The next page could not be read.</span> : null}
          {read.canLoadMore ? (
            <ActionButton
              disabled={read.loadingMore}
              onClick={read.loadMore}
              data-testid="scope-figures-show-more"
            >
              {read.loadingMore
                ? "Reading more…"
                : `Show ${quantity(String(Math.min(FIGURES_PAGE, unread)))} more`}
            </ActionButton>
          ) : null}
        </div>
      ) : null}
      </>
    ),
  };
}
